"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  type BusinessPlanInput,
  buildBusinessPlan,
  type PlanFormat,
} from "@/lib/tool-kit/business-templates/business-plan";

/** English strings. A translated content file overrides these through `ui`. */
export const BUSINESS_PLAN_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  formatLabel: "Kind of plan",
  formats: {
    traditional: "Traditional plan",
    competition: "Competition or pitch plan",
  },
  formatHints: {
    traditional:
      "The eight sections the US Small Business Administration lists for a traditional plan, plus an appendix.",
    competition:
      "Nine sections in pitch order — problem, solution, market, model, go-to-market, team, financials, ask — for a class or competition plan.",
  },
  projectHeading: "Your business",
  nameLabel: "Business name or working title",
  namePlaceholder: "e.g. Pedal Pals Bike Repair",
  offerLabel: "What you sell, in your own words",
  offerPlaceholder: "e.g. Fast, low-price bike repairs at a stand on campus.",
  customersLabel: "Who buys it, and why",
  customersPlaceholder:
    "e.g. Students and staff who cycle to campus and cannot wait days for a shop.",
  askLabel: "What you are asking for (optional)",
  askPlaceholder: "e.g. £5,000 for tools, parts and a permit.",
  wordsLabel: "Target length (words)",
  wordsPlaceholder: "e.g. 3000",
  wordsHint:
    "Splits the plan into a word target for each section. Tables and the appendix are not counted.",
  fileName: "business-plan-outline",
  document: {
    heading: "Your business plan outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: prompt still to answer|other: prompts still to answer",
    complete: "Every prompt has been answered.",
  },
  defaultTitle: "Business Plan Outline",
  titleWithName: "Business Plan: {name}",
  subtitles: {
    traditional:
      "Traditional plan — sections as listed by the US Small Business Administration",
    competition: "Competition or pitch plan",
  },
  subtitleWords: "{words} words",
  budgetHeading: "Word Budget",
  budgetNote:
    "A planning convention, not a standard. If your competition or course sets its own sections or page limit, follow that and use these shares only to balance the length.",
  budget: {
    section: "Section",
    share: "Share",
    words: "Target words",
    total: "Total",
  },
  sectionNames: {
    summary: "Executive Summary",
    company: "Company Description",
    market: "Market Analysis",
    organization: "Organization and Management",
    product: "Service or Product Line",
    marketing: "Marketing and Sales",
    funding: "Funding Request",
    financials: "Financial Projections",
    problem: "Problem and Opportunity",
    solution: "Solution",
    model: "Business Model",
    gotomarket: "Go-to-Market Plan",
    team: "Team and Operations",
    ask: "The Ask and Milestones",
  },
  tips: {
    summary:
      "Write this last. It tells a reader who will only read one page what the business is and why it will succeed.",
    company:
      "State what the company is, who owns it, and where it stands today.",
    market:
      "This is where most plans are judged. Back every figure with a source and name your competitors.",
    organization: "Show that the people can deliver and who is in charge.",
    product:
      "Describe what you sell in terms of what it does for the customer, not its features.",
    marketing: "Show how you will win customers, and what it will cost.",
    funding:
      "Skip this section if you are not asking for money, or say what you need instead.",
    financials:
      "Put tables here and keep the words for assumptions. Every number should trace to a stated assumption.",
    problem:
      "Show that a real group of people has a real problem, with evidence.",
    solution:
      "Say what you will build or sell and why it beats the current way.",
    model:
      "Say who pays, how much, and what is left after the costs of serving them.",
    gotomarket: "Show how you will win your first customers, not all of them.",
    team: "Show why this team, and how the business will run day to day.",
    ask: "End on a specific request and what it buys.",
  },
  prompts: {
    summary: [
      "What is the business, in one sentence?",
      "Who are the customers, and what problem do you solve for them?",
      "Why will it succeed — what is your advantage, and what is already proven?",
      "What do you need, and what will it achieve?",
    ],
    company: [
      "What is the legal structure, and where is the business based?",
      "What is the mission, and what are the goals for year one and year three?",
      "What stage is it at — idea, prototype, first sales?",
    ],
    market: [
      "How big is the market, and is it growing? Cite a source for each figure.",
      "Who exactly is the target customer — who, where, and how many?",
      "Who are the competitors, and what do customers use instead?",
      "What gap in the market are you filling?",
    ],
    organization: [
      "Who are the founders and key people, and what have they done that fits this business?",
      "Who owns what, and who decides?",
      "Which roles are missing, and how will you fill them?",
    ],
    product: [
      "What exactly are you selling, and what does it do for the customer?",
      "What stage is it at, and what is left to build or test?",
      "What makes it hard to copy, if anything?",
    ],
    marketing: [
      "How will customers hear about you, and what will that cost?",
      "How will you price it, and why that price?",
      "How will you sell it and keep customers coming back?",
    ],
    funding: [
      "How much do you need, and over what period?",
      "What will the money be spent on?",
      "What will the business have achieved when it is spent?",
    ],
    financials: [
      "What are the sales forecasts, and the assumptions behind each?",
      "What are the start-up and running costs?",
      "When does the business break even, and when does it need cash most?",
    ],
    problem: [
      "Who has the problem, and how often?",
      "What do they do about it today, and what does that cost them?",
      "What evidence — interviews, surveys, data — shows the problem is real?",
    ],
    solution: [
      "What is the product, in plain words?",
      "Why is it better than the current way — faster, cheaper, safer?",
      "What exists today — a prototype, a pilot, paying users?",
    ],
    model: [
      "Who pays, how much, and how often?",
      "What does it cost to serve one customer?",
      "What does one customer contribute after those costs?",
    ],
    gotomarket: [
      "How will you win your first ten customers?",
      "Which channels will you use, and what will each cost?",
      "What will you measure to know it is working?",
    ],
    team: [
      "Who is on the team, and why them?",
      "What skills are missing, and who could fill them — advisers, hires?",
      "What have you already done that shows you can deliver?",
    ],
    ask: [
      "How much money, prize or support are you asking for?",
      "What exactly will it be spent on?",
      "What milestones will it reach, and by when?",
    ],
  },
  target: "Target: {words} words ({share}).",
  targetShare: "About {share} of the plan.",
  appendixName: "Appendix",
  appendixTip:
    "Supporting material that would clog the main text: résumés, permits, product images, detailed tables, and full source lists. It does not count towards the word target.",
  checklistHeading: "Before You Submit",
  checklist: [
    "The executive summary was written last and stands on its own",
    "Every market figure has a source",
    "Financial assumptions are stated, and the numbers agree between sections",
    "The plan follows the competition's or course's own headings and length limit",
    "A reader who has never heard of the business could follow it",
  ],
  example: {
    name: "Pedal Pals Bike Repair",
    offer:
      "Pedal Pals repairs and services bikes at a stand on campus, between classes, at prices below the nearest bike shop.",
    customers:
      "Students and staff who cycle to campus and cannot leave their bike at a shop for several days.",
    ask: "£5,000 for tools, spare parts, a trading permit and insurance.",
    words: "3000",
  },
} as const;

const EMPTY: BusinessPlanInput = {
  name: "",
  offer: "",
  customers: "",
  ask: "",
  words: "",
  format: "traditional",
};

type TextKey = "name" | "offer" | "customers" | "ask" | "words";

export default function BusinessPlanOutlineGeneratorWidget() {
  const strings = useToolUi(BUSINESS_PLAN_OUTLINE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<BusinessPlanInput>(EMPTY);
  const doc = useMemo(
    () => buildBusinessPlan(input, strings, fmt),
    [input, strings, fmt]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              format: previous.format,
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.projectHeading}>
        <PillGroup<PlanFormat>
          label={strings.formatLabel}
          name="bp-format"
          onChange={(format) =>
            setInput((previous) => ({ ...previous, format }))
          }
          options={(["traditional", "competition"] as const).map((value) => ({
            value,
            label: strings.formats[value],
          }))}
          value={input.format}
        />
        <p className="text-muted-foreground text-xs">
          {strings.formatHints[input.format]}
        </p>
        <TextField
          id="bp-name"
          label={strings.nameLabel}
          onChange={update("name")}
          placeholder={strings.namePlaceholder}
          value={input.name}
        />
        <TextAreaField
          id="bp-offer"
          label={strings.offerLabel}
          onChange={update("offer")}
          placeholder={strings.offerPlaceholder}
          prose
          rows={3}
          value={input.offer}
        />
        <TextAreaField
          id="bp-customers"
          label={strings.customersLabel}
          onChange={update("customers")}
          placeholder={strings.customersPlaceholder}
          prose
          rows={3}
          value={input.customers}
        />
        <TextAreaField
          id="bp-ask"
          label={strings.askLabel}
          onChange={update("ask")}
          placeholder={strings.askPlaceholder}
          prose
          rows={2}
          value={input.ask}
        />
        <TextField
          hint={strings.wordsHint}
          id="bp-words"
          label={strings.wordsLabel}
          numeric
          onChange={update("words")}
          placeholder={strings.wordsPlaceholder}
          value={input.words}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="bp"
        labels={strings.document}
      />
    </div>
  );
}
